import {
  useProxyTypeUrlState,
  useProxySubtypeUrlState,
  useCountriesUrlState,
  useIsHighIpReputationUrlState,
  usePlanIdUrlState,
} from "@/hooks/urlState";

import { backendFormatCountryList } from "@/utils/unallocatedProxyUtils";
import useSWR from "@/hooks/useSWR";
import {
  SubscriptionCustomizeRequestQueryType,
  SubscriptionCustomizeType,
} from "@/types/SubscriptionTypes";
import { getSelectedBackendPlanSubCategory } from "@/components/subscription/customize/checkout/utils/rotatingPlanUtils";
import useGetTotalNumProxies from "@/components/subscription/customize/hooks/useGetTotalNumProxies";

import logToSentry from "@/utils/logToSentry";
import { urlWithPlanId } from "@/utils/toPlanId";
import { AxiosError, isAxiosError } from "axios";
import useAllowedActivePlans from "@/hooks/useAllowedActivePlans";
import { markPlanBackendDenied } from "@/hooks/useBackendDeniedPlanIds";
import useSubuserId from "@/hooks/useSubuserId";
import WebshareApi from "@/api/index";

/**
 * Errors that are expected and handled elsewhere: offline users, and 403s for
 * suspended/deleted accounts (RouteGuard routes those users to the dedicated
 * pages; the fetch just raced the redirect).
 */
function isExpectedCustomizeError(error: AxiosError) {
  if (["ECONNABORTED", "ERR_NETWORK"].includes(error.code || "")) return true;
  const code = (error.response?.data as { code?: string } | undefined)?.code;
  return (
    error.status === 403 &&
    (code === "account_suspended" || code === "account_deleted")
  );
}

/**
 * A 403 outside any subuser context means the backend's PlanPermission
 * disowned the plan itself. Under a subuser context it instead means a stale
 * X-Subuser header (R-1856) — `useSubuser` exits that context on its own and
 * the key change refetches.
 */
function isPlanPermission403(error: AxiosError, subuserId: string | null) {
  return (
    error.status === 403 &&
    !subuserId &&
    WebshareApi.getSubUserId() === undefined
  );
}

export default function useCustomize() {
  const [proxyType] = useProxyTypeUrlState();
  const [proxySubtype] = useProxySubtypeUrlState();
  const [countries] = useCountriesUrlState();
  const [isHighIpReputation] = useIsHighIpReputationUrlState();
  const { totalNumberOfProxies } = useGetTotalNumProxies();
  const [planId] = usePlanIdUrlState();
  const { activePlans, isLoading: isPlansLoading } = useAllowedActivePlans();
  const subuserId = useSubuserId();

  const getKey = () => {
    if (!proxyType || !proxySubtype) return { url: "", enabled: false };

    // Skip if planId is stale/unowned — backend 403s. PROXYCONTROLPANELDASHBOARD-5JR.
    if (planId && planId !== "default") {
      if (isPlansLoading) return { url: "", enabled: false };
      const userOwnsPlan = activePlans.some((p) => p.id === planId);
      if (!userOwnsPlan) return { url: "", enabled: false };
    }

    const query: SubscriptionCustomizeRequestQueryType = {
      proxy_type: proxyType,
      proxy_subtype: getSelectedBackendPlanSubCategory({
        selectedPlanSubCategory: proxySubtype,
        selectedPlanCategory: proxyType,
      }),
    };
    if (totalNumberOfProxies && proxySubtype !== "residential") {
      query.proxy_countries = backendFormatCountryList(
        countries,
        totalNumberOfProxies
      );
    }
    if (isHighIpReputation) {
      query.high_quality_ips_only = true;
    }

    return {
      // The subuser id is part of the key on purpose: the request rides the
      // X-Subuser header either way, and a 403 caused by a stale subuser
      // context (R-1856) can only refetch when leaving that context changes
      // the key — SWR never retries 4xx on its own (see onErrorRetry).
      url: urlWithPlanId(`/subscription/customize/`, {
        plan_id: planId ? planId : "default",
        query: JSON.stringify(query),
      }),
      subUserId: subuserId ?? undefined,
    };
  };

  return useSWR<SubscriptionCustomizeType>(getKey(), {
    revalidateIfStale: false,
    // revalidateOnMount: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    onErrorRetry: (error, _key, _config, revalidate, { retryCount }) => {
      // 4xx won't recover — retrying just multiplies Sentry noise.
      const status = isAxiosError(error) ? error.response?.status : undefined;
      if (status && status >= 400 && status < 500) return;
      if (retryCount >= 5) return;
      setTimeout(() => revalidate({ retryCount }), 5000);
    },
    onError: (error) => {
      if (!isAxiosError(error)) {
        logToSentry("customize error - unknown", {
          extras: [
            ["errorData", JSON.stringify(error)],
            ["message", error?.message],
          ],
        });
        return;
      }
      if (isExpectedCustomizeError(error)) return;
      if (
        planId &&
        planId !== "default" &&
        isPlanPermission403(error, subuserId)
      ) {
        // The plan list said we own this plan, the backend's PlanPermission
        // said no (R-1856, e.g. a plan detached from the subscription mid
        // plan-change). Trust the backend so ownership resolution retargets
        // or drops the planId instead of dead-ending on the error banner.
        markPlanBackendDenied(planId);
      }
      // 429s keep a distinct message: they have their own issue (gitlab
      // frontend issue #2406) but we still want eyes on them.
      const message =
        error.status === 429
          ? "customize rate limit error"
          : `customize error - ${error.response?.status || -1}`;
      logToSentry(message, {
        extras: [
          ["errorData", JSON.stringify(error)],
          ["responseData", JSON.stringify(error.response?.data)],
        ],
      });
    },
  });
}
